import React, {
  CSSProperties,
  TransitionEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Box } from '@tanium/react-box';
import styled from '@tanium/react-emotion';
import { useExpansionHeight } from '@tanium/react-use-expansion-height';
import { usePreviousValue } from '@tanium/react-use-previous-value';
import { css, SpaceStyleType } from '@tanium/style-system';

enum TransitionState {
  Collapsing,
  Expanding,
  Collapsed,
  Expanded,
}

export interface Props {
  /**
   * Whether content should be displayed
   */
  isCollapsed: boolean;
  /**
   * The content of the section.
   */
  children?: React.ReactNode;
  /**
   * The content of the section when it's collapsed.
   */
  collapsedContent?: React.ReactNode;
  /**
   * Overrides the standard Y-axis padding of the container around the children.
   */
  childContainerPaddingY?: SpaceStyleType;
}

const CollapsedContent = styled(Box<'div'>, {
  shouldForwardProp: (p) => !['isCollapsedOrCollapsing', 'isTransitioning'].includes(p),
})<{
  isCollapsedOrCollapsing: boolean;
  isTransitioning: boolean;
  style: CSSProperties;
}>(
  css({
    transition: 'height .3s, opacity .3s',
  }),
  ({ isTransitioning }) => ({
    overflow: isTransitioning ? 'visible' : 'hidden',
  }),
  ({ isCollapsedOrCollapsing }) => ({
    opacity: isCollapsedOrCollapsing ? 1 : 0,
  }),
);

const Content = styled(Box<'div'>, {
  shouldForwardProp: (p) => !['isCollapsedOrCollapsing', 'isTransitioning'].includes(p),
})<{
  isCollapsedOrCollapsing: boolean;
  isTransitioning: boolean;
  onTransitionEnd: TransitionEventHandler;
  style: CSSProperties;
}>(
  css({
    transition: 'height .3s, opacity .3s',
  }),
  ({ isTransitioning }) => ({
    overflow: isTransitioning ? 'hidden' : 'visible',
  }),
  ({ isCollapsedOrCollapsing }) => ({
    opacity: isCollapsedOrCollapsing ? 0 : 1,
  }),
);

/**
 * Content for PageSection
 */
export const PageSectionContent = ({
  children,
  childContainerPaddingY = 1,
  collapsedContent,
  isCollapsed: isCollapsedProp,
}: Props) => {
  // Only remove child content after collapsing animation is complete.
  const [transitionState, setTransitionState] = useState<TransitionState>(
    isCollapsedProp ? TransitionState.Collapsed : TransitionState.Expanded,
  );
  const wasCollapsed = usePreviousValue(isCollapsedProp);

  const isCollapsed = transitionState === TransitionState.Collapsed;
  const isExpanded = transitionState === TransitionState.Expanded;
  const isTransitioning =
    transitionState === TransitionState.Collapsing || transitionState === TransitionState.Expanding;
  const isCollapsedOrCollapsing =
    transitionState === TransitionState.Collapsing || transitionState === TransitionState.Collapsed;

  const { ref: contentRef, height } = useExpansionHeight(!isCollapsedOrCollapsing);
  const { ref: collapsedContentRef, height: collapsedContentHeight } =
    useExpansionHeight(isCollapsedOrCollapsing);

  useEffect(() => {
    if (isCollapsedProp !== wasCollapsed) {
      setTransitionState(isCollapsedProp ? TransitionState.Collapsing : TransitionState.Expanding);
    }
  }, [isCollapsedProp, wasCollapsed]);

  const handleTransitionEnd = useCallback(() => {
    // `transitionend` fires once for each property being transitioned, so we cannot assume that the
    // transition state is necessarily either collapsing or expanding.
    setTransitionState((oldState) => {
      if (oldState === TransitionState.Collapsing) {
        return TransitionState.Collapsed;
      }

      if (oldState === TransitionState.Expanding) {
        return TransitionState.Expanded;
      }

      return oldState;
    });
  }, []);

  const contentStyle = useMemo(
    () => ({
      height: isCollapsedOrCollapsing ? 0 : (height !== undefined && `${height}px`) || 'auto',
    }),
    [height, isCollapsedOrCollapsing],
  );

  const collapsedContentStyle = useMemo(
    () => ({
      height: isCollapsedOrCollapsing
        ? (collapsedContentHeight !== undefined && `${collapsedContentHeight}px`) || 'auto'
        : 0,
    }),
    [collapsedContentHeight, isCollapsedOrCollapsing],
  );

  return (
    <>
      {collapsedContent && (
        <CollapsedContent
          style={collapsedContentStyle}
          isCollapsedOrCollapsing={isCollapsedOrCollapsing}
          isTransitioning={isTransitioning}
        >
          {!isExpanded && (
            <div ref={collapsedContentRef}>
              <Box py={1}>{collapsedContent}</Box>
            </div>
          )}
        </CollapsedContent>
      )}
      {children && (
        <Content
          style={contentStyle}
          isCollapsedOrCollapsing={isCollapsedOrCollapsing}
          isTransitioning={isTransitioning}
          onTransitionEnd={handleTransitionEnd}
        >
          {!isCollapsed && (
            <div ref={contentRef}>
              <Box py={childContainerPaddingY}>{children}</Box>
            </div>
          )}
        </Content>
      )}
    </>
  );
};
